import React, { useEffect, useState } from 'react';
import { Button, Card, Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import Select from "react-select";
import makeAnimated from "react-select/animated";
import { toast } from 'react-toastify';
import Header from '../../layouts/Header';
import axios from '../../api/axios';

const RMDamageEntries = () => {
    const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : '';
    const [skin, setSkin] = useState(currentSkin);
    
    const accessToken = localStorage.getItem("authToken");
    const animatedComponents = makeAnimated();
    const [storageLocations, setStorageLocations] = useState([]);
    const [formData, setFormData] = useState({
        storageLocation: "",
        remark: "",
        centralStockTransferData: []
    });
    const [rawMaterials, setRawMaterials] = useState([]);
    const [selectedRawMaterial, setSelectedRawMaterial] = useState(null);
    const [transferQty, setTransferQty] = useState("");
    const [selectedRawMaterialAddMore, setSelectedRawMaterialAddMore] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    // Fetch Storage Locations
    useEffect(() => {
        getNewStorageLocationData();
    }, []);

    const getNewStorageLocationData = async () => {
        try {
            const headers = {
                Authorization: `Bearer ${accessToken}`,
            };
            const response = await axios.get("storage-location/getCurrentStock", { headers });
            if (response?.data?.data?.storageLocations) {
                setStorageLocations(response?.data?.data?.storageLocations);
            }
        } catch (error) {
            toast.error("Failed to fetch storage location data.", {
                position: "top-right",
                autoClose: 3000,
                theme: "colored",
            });
        }
    };

    const storageLocationOptions = storageLocations.map((location) => ({
        value: location._id,
        label: `${location.name} - ${location.city}, ${location.state}`,
    }));

    // Handle Storage Location Selection
    const handleStorageLocationChange = (option) => {
        setFormData({
            ...formData,
            storageLocation: option?.value || "",
            centralStockTransferData: [],
        });

        const selectedStorage = storageLocations.find((loc) => loc._id === option?.value);
        setRawMaterials(selectedStorage?.stockInfo || []);
    };

    // Raw Material Options
    const rawMaterialOptions = rawMaterials
        .filter((material) => 
            !formData.centralStockTransferData.some(
                (item) => item.rawMaterialId === material.rawMaterial._id
            )
        )
        .map((material) => ({
            value: material.rawMaterial._id,
            label: material.rawMaterial.name,
            unit: material.rawMaterial.unit,
            currentStock: material.currentStock,
        }));

    const rawMaterialOptionsAddMore = rawMaterials
        .filter((material) => 
            !formData.centralStockTransferData.some(
                (item) => item.rawMaterialId === material.rawMaterial._id
            )
        )
        .map((material) => ({
            value: material.rawMaterial._id,
            label: material.rawMaterial.name,
            unit: material.rawMaterial.unit,
            currentStock: material.currentStock,
        }));

    // Handle Raw Material Selection
    const handleRawMaterialChange = (option) => {
        setSelectedRawMaterial(option);
        setTransferQty("");
    };

    // Handle Transfer Quantity Change
    const handleTransferQtyChange = (e) => {
        setTransferQty(e.target.value);
    };

    // Add Raw Material Entry
    const handleAddMore = () => {
        if (!selectedRawMaterial || !transferQty) {
            toast.error("Please select a raw material and enter transfer quantity.", {
                position: "top-right",
                autoClose: 2000,
                theme: "colored",
            });
            return;
        }

        const newEntry = {
            rawMaterialId: selectedRawMaterial.value,
            rawMaterialName: selectedRawMaterial.label,
            unit: selectedRawMaterial.unit,
            currentStockQuantity: selectedRawMaterial.currentStock,
            transferQuantity: transferQty,
        };

        setFormData({
            ...formData,
            centralStockTransferData: [...formData.centralStockTransferData, newEntry],
        });

        setSelectedRawMaterial(null);
        setTransferQty("");
    };

    const handleSubmit = async (e) => {
        setIsLoading(true);
        e.preventDefault();
        try {
            const headers = {
                Authorization: `Bearer ${accessToken}`,
            };

            const cleanedFormData = {
                ...formData,
                centralStockTransferData: formData.centralStockTransferData.map(item => {
                    const { unit, rawMaterialName, currentStockQuantity, ...rest } = item;
                    return rest;
                })
            };

            const response = await axios.post("/stock-transactions/debitDamageStockFromCentral", cleanedFormData, { headers });
            if (response?.data?.status === true) {
                    toast.success("Stock transfer successful!", {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "colored",
                });
                setSelectedRawMaterial(null);
                setFormData({
                    storageLocation: "",
                    remark: "",
                    centralStockTransferData: [],
                });
                getNewStorageLocationData();
            }
        } catch (error) {
            let errorMessage = '';
            if (error.response?.status === 406) {
                errorMessage = error?.response?.data?.error?.message || error?.response?.data?.error
            }
            else if (error.response?.status === 412) {
                errorMessage = error?.response?.data?.error;
            } else if (error.response?.status === 500) {
                errorMessage = error?.response?.data?.error
            }
            toast.error(errorMessage, {
                position: "top-right",
                autoClose: 3000,
                theme: "colored",
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <React.Fragment>
            <Header onSkin={setSkin} />
            <div className="main main-app p-3 p-lg-4">
                <Card className='mb-3'>
                    <Card.Header className="bg-danger text-white d-flex align-items-center p-3">
                        <i className="bi bi-exclamation-octagon-fill text-white fs-5 me-2"></i>
                        RAW MATERIALS DAMAGE ENTRIES
                    </Card.Header>
                    <Card.Body>
                        <Form onSubmit={handleSubmit}>
                            <Row>
                                <Col md={12} lg={12}>
                                    <Form.Label className="fw-semibold">
                                        Select Storage Location <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Select
                                        components={animatedComponents}
                                        options={storageLocationOptions}
                                        placeholder="All Storage Locations"
                                        isClearable
                                        isDisabled={selectedRawMaterial || formData?.centralStockTransferData?.length > 0}
                                        onChange={handleStorageLocationChange}
                                        value={storageLocationOptions.find((option) => option.value === formData.storageLocation) || null}
                                    />
                                </Col>
                            </Row>
                            <hr />
                            <Row className='mb-3'>
                                <Col lg={5}>
                                    <Form.Label className='fw-semibold'>
                                        Select Raw Material <span className='text-danger'>*</span>
                                    </Form.Label>
                                    <Select
                                        isClearable
                                        options={rawMaterialOptions}
                                        placeholder="Select Raw Materials"
                                        value={selectedRawMaterial}
                                        onChange={handleRawMaterialChange}
                                    />
                                </Col>
                                <Col md={2}>
                                    <Form.Label className="fw-semibold">
                                        Current Qty <span className='text-danger'>*</span>
                                    </Form.Label>
                                    <Form.Control
                                        disabled
                                        type="text"
                                        value={
                                            selectedRawMaterial?.currentStock 
                                                ? `${selectedRawMaterial?.currentStock?.toFixed(2)} ${selectedRawMaterial.unit || ""}`
                                                : ""
                                        }
                                    />
                                </Col>
                                <Col md={2}>
                                    <Form.Label className="fw-semibold">
                                        Transfer Damage Qty <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="number"
                                        min="0"
                                        step="any"
                                        value={transferQty}
                                        onChange={handleTransferQtyChange}
                                    />
                                </Col>
                                <Col md={3}>
                                    <div className='d-flex flex-column'>
                                        <Form.Label className="text-white fw-semibold">.</Form.Label>
                                        <Button
                                            size="sm"
                                            className="btn-circle me-2 w-50"
                                            variant="outline-dark"
                                            onClick={handleAddMore}
                                        >
                                            + ADD MORE
                                        </Button>
                                    </div>
                                </Col>
                            </Row>

                            {/* Display Selected Raw Materials */}
                            {formData?.centralStockTransferData?.length > 0 && (
                                <div className="mt-3">
                                    <h6 className='text-primary' style={{ textDecoration: 'underline' }}>Selected Raw Materials</h6>
                                    {formData?.centralStockTransferData?.map((item, index) => (
                                        <Row className="mb-2 align-items-center" key={index}>
                                            <Col lg={4}>
                                                <Form.Label className="fw-semibold text-muted">Raw Material</Form.Label>
                                                <Select
                                                    options={rawMaterials?.map((rm) => ({
                                                            value: rm.rawMaterial._id,
                                                            label: rm.rawMaterial.name,
                                                            unit: rm.rawMaterial.unit,
                                                            currentStock: rm.currentStock,
                                                        })
                                                    )}
                                                    value={item?.rawMaterialId ? { value: item?.rawMaterialId, label: item?.rawMaterialName } : selectedRawMaterialAddMore}
                                                    onChange={(selectedOption) => {
                                                        setSelectedRawMaterialAddMore(selectedOption);
                                                        const updatedData = [...formData.centralStockTransferData];
                                                        updatedData[index] = {
                                                            ...updatedData[index],
                                                            rawMaterialId: selectedOption?.value || "",
                                                            rawMaterialName: selectedOption?.label || "",
                                                            currentStockQuantity: selectedOption?.currentStock || "",
                                                            unit: selectedOption?.unit || "",
                                                            transferQuantity: "",
                                                        };
                                                        setFormData({ ...formData, centralStockTransferData: updatedData });
                                                    }}
                                                    isClearable
                                                    placeholder="Select Raw Material"
                                                />
                                            </Col>
                                            <Col lg={2}>
                                                <Form.Label className="fw-semibold text-muted">Current Qty</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    value={item?.currentStockQuantity?.toFixed(2)}
                                                    readOnly
                                                    disabled
                                                />
                                            </Col>
                                            <Col lg={2}>
                                                <Form.Label className="fw-semibold text-muted">Unit</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    value={item.unit}
                                                    readOnly
                                                    disabled
                                                />
                                            </Col>
                                            <Col lg={2}>
                                                <Form.Label className="fw-semibold text-muted">Transfer Damage Qty</Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    min="0"
                                                    step="any"
                                                    value={item.transferQuantity}
                                                    onChange={(e) => {
                                                        const updatedData = [...formData.centralStockTransferData];
                                                        updatedData[index].transferQty = e.target.value;
                                                        setFormData({ ...formData, centralStockTransferData: updatedData });
                                                    }}
                                                />
                                            </Col>
                                            <Col lg={2}>
                                                <Button
                                                    size="sm"
                                                    variant="outline-danger"
                                                    className="mt-4"
                                                    onClick={() => {
                                                        const updatedData = formData.centralStockTransferData.filter((_, i) => i !== index);
                                                        setFormData({ ...formData, centralStockTransferData: updatedData });
                                                    }}
                                                >
                                                    Remove
                                                </Button>
                                            </Col>
                                        </Row>
                                    ))}
                                </div>
                            )}

                            <hr />
                            <Row className='mb-4'>
                                <Col lg={12}>
                                    <Form.Label className='fw-semibold'>
                                        Remark <span className='text-danger'>*{" "}</span><span className='fw-normal text-danger' style={{ fontSize: '13px' }}>(Enter Atleast 50 Characters)</span>
                                    </Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={2}
                                        placeholder="Enter Remark"
                                        name="remark"
                                        value={formData.remark}
                                        onChange={(e) => setFormData({ ...formData, remark: e.target.value })}
                                    />
                                </Col>
                            </Row>

                            <hr />
                            <Row className='mt-4'>
                                <Col>
                                    <Button className='w-25' variant="primary" type="submit">
                                        {isLoading ? <Spinner size='sm' animation="border" /> : "SUBMIT ENTRY"}
                                    </Button>
                                </Col>
                            </Row>
                        </Form>
                    </Card.Body>
                </Card>
            </div>
        </React.Fragment>
    );
}

export default RMDamageEntries;